import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { playSound, speak } from '../utils/sounds';
import { getSuccessMessage, getErrorMessage } from '../utils/encouragements';
import RewardCounter from '../components/RewardCounter';

const CollectionComparison = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('introduction'); // introduction, grouping, counting-groups, counting-isolated, comparison
  const [collections, setCollections] = useState([]);
  const [currentCollectionIndex, setCurrentCollectionIndex] = useState(0);
  const [selectedObjects, setSelectedObjects] = useState([]); // Objets sélectionnés pour le groupe en cours
  const [userGroups, setUserGroups] = useState([[], []]); // Groupes créés par l'utilisateur pour chaque collection
  const [groupCounts, setGroupCounts] = useState([]);
  const [isolatedCounts, setIsolatedCounts] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawPath, setDrawPath] = useState([]);
  const canvasRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const objectPositionsRef = React.useRef({});
  const hasSpokenRef = React.useRef(false);

  // Emojis pour les collections
  const objectEmojis = ['🍎', '🍊', '🍋', '🍌', '🍇', '🍓', '🍒', '🥕', '🌽', '🥦', '🧸', '🎨', '📚', '⚽', '🎵'];

  const generateExercise = useCallback(() => {
    // Générer 2 collections avec des objets différents
    const shuffled = [...objectEmojis].sort(() => Math.random() - 0.5);
    const emoji1 = shuffled[0];
    const emoji2 = shuffled[1];

    // Collection 1: entre 10 et 25 objets
    const total1 = Math.floor(Math.random() * 16) + 10; // 10 à 25

    // Collection 2: différente de la collection 1
    let total2;
    do {
      total2 = Math.floor(Math.random() * 16) + 10;
    } while (total2 === total1);

    // Créer les objets individuels
    const objects1 = Array.from({ length: total1 }, (_, i) => ({ id: `c1-${i}`, emoji: emoji1 }));
    const objects2 = Array.from({ length: total2 }, (_, i) => ({ id: `c2-${i}`, emoji: emoji2 }));

    setCollections([
      { emoji: emoji1, objects: objects1, total: total1 },
      { emoji: emoji2, objects: objects2, total: total2 }
    ]);
    setUserGroups([[], []]);
    setGroupCounts([]);
    setIsolatedCounts([]);
    setCurrentCollectionIndex(0);
    setSelectedObjects([]);
    setStep('introduction');
    setSelectedAnswer(null);
    setFeedback(null);
    hasSpokenRef.current = false;

    setTimeout(() => {
      if (!hasSpokenRef.current) {
        speak('Observe bien les deux collections. Tu vas devoir regrouper les objets par 5 !');
        hasSpokenRef.current = true;
      }
    }, 500);
  }, []);

  useEffect(() => {
    generateExercise();
  }, [generateExercise]);

  const startGrouping = () => {
    playSound('click');
    setStep('grouping');
    speak(`Entoure des objets par groupe de 5 dans la collection ${currentCollectionIndex + 1}. Dessine un cercle autour de 5 objets avec ta souris.`);
  };

  const getPointerPosition = (e) => {
    if (!containerRef.current) return null;

    const rect = containerRef.current.getBoundingClientRect();
    let clientX, clientY;

    if (e.touches && e.touches.length > 0) {
      // Touch event
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const handlePointerDown = (e) => {
    if (step !== 'grouping' || !containerRef.current) return;

    // Empêcher le scroll sur mobile
    e.preventDefault();

    const pos = getPointerPosition(e);
    if (!pos) return;

    setIsDrawing(true);
    setDrawPath([pos]);
  };

  const handlePointerMove = (e) => {
    if (!isDrawing || !containerRef.current) return;

    // Empêcher le scroll sur mobile
    e.preventDefault();

    const pos = getPointerPosition(e);
    if (!pos) return;

    setDrawPath(prev => [...prev, pos]);

    // Dessiner sur le canvas
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.strokeStyle = '#667eea';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';

      if (drawPath.length > 0) {
        const lastPoint = drawPath[drawPath.length - 1];
        ctx.beginPath();
        ctx.moveTo(lastPoint.x, lastPoint.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
      }
    }
  };

  const handlePointerUp = (e) => {
    if (!isDrawing) return;

    // Empêcher le scroll sur mobile
    e.preventDefault();

    setIsDrawing(false);

    // Vérifier quels objets sont à l'intérieur du tracé
    const enclosedObjects = getEnclosedObjects();

    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    setDrawPath([]);

    // Vérifier si on a exactement 5 objets
    if (enclosedObjects.length === 5) {
      playSound('success');
      const currentUserGroups = userGroups[currentCollectionIndex];
      const newUserGroups = [...userGroups];
      newUserGroups[currentCollectionIndex] = [...currentUserGroups, enclosedObjects];
      setUserGroups(newUserGroups);
      speak('Super ! Un groupe de 5 est créé. Continue à entourer des groupes de 5 ou clique sur "Terminer".');
    } else if (enclosedObjects.length > 0) {
      playSound('error');
      speak(`Tu as entouré ${enclosedObjects.length} objet${enclosedObjects.length > 1 ? 's' : ''}. Il faut exactement 5 objets par groupe !`);
    }
  };

  const getEnclosedObjects = () => {
    const currentCollection = collections[currentCollectionIndex];
    const currentUserGroups = userGroups[currentCollectionIndex];
    const enclosed = [];

    currentCollection.objects.forEach(obj => {
      // Vérifier si l'objet est déjà dans un groupe
      const isInGroup = currentUserGroups.some(group => group.includes(obj.id));
      if (isInGroup) return;

      const pos = objectPositionsRef.current[obj.id];
      if (pos && isPointInPath(pos.x, pos.y)) {
        enclosed.push(obj.id);
      }
    });

    return enclosed;
  };

  const isPointInPath = (x, y) => {
    if (drawPath.length < 3) return false;

    // Algorithme du rayon (ray casting) pour vérifier si un point est dans un polygone
    let inside = false;
    for (let i = 0, j = drawPath.length - 1; i < drawPath.length; j = i++) {
      const xi = drawPath[i].x, yi = drawPath[i].y;
      const xj = drawPath[j].x, yj = drawPath[j].y;

      const intersect = ((yi > y) !== (yj > y))
        && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }

    return inside;
  };

  useEffect(() => {
    if (canvasRef.current && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      canvasRef.current.width = rect.width;
      canvasRef.current.height = rect.height;
    }
  }, [step, currentCollectionIndex]);

  const finishGrouping = () => {
    playSound('click');
    const currentUserGroups = userGroups[currentCollectionIndex];
    const currentCollection = collections[currentCollectionIndex];
    const groupedObjectsCount = currentUserGroups.reduce((acc, group) => acc + group.length, 0);
    const remainingCount = currentCollection.total - groupedObjectsCount;

    if (selectedObjects.length > 0) {
      speak('Tu as des objets sélectionnés. Finis le groupe de 5 ou désélectionne-les.');
      return;
    }

    setStep('counting-groups');
    speak(`Parfait ! Maintenant, combien as-tu fait de groupes de 5 ?`);
  };

  const handleGroupCount = (count) => {
    playSound('click');
    const currentUserGroups = userGroups[currentCollectionIndex];
    const actualGroupCount = currentUserGroups.length;

    if (count === actualGroupCount) {
      playSound('success');
      setFeedback({ type: 'success', message: `✅ Oui ! ${count} groupe${count > 1 ? 's' : ''} de 5 !` });
      const newGroupCounts = [...groupCounts, count];
      setGroupCounts(newGroupCounts);

      setTimeout(() => {
        setFeedback(null);
        setStep('counting-isolated');
        const groupedObjectsCount = currentUserGroups.reduce((acc, group) => acc + group.length, 0);
        const remainingCount = collections[currentCollectionIndex].total - groupedObjectsCount;
        speak(`Maintenant, compte les objets qui ne sont pas dans un groupe de 5`);
      }, 2000);
    } else {
      playSound('error');
      setIncorrectCount(prev => prev + 1);
      setFeedback({ type: 'error', message: `❌ Recompte les groupes de 5 que tu as entourés` });
      speak('Réessaie ! Compte bien les groupes de 5 que tu as faits');
    }
  };

  const handleIsolatedCount = (count) => {
    playSound('click');
    const currentUserGroups = userGroups[currentCollectionIndex];
    const currentCollection = collections[currentCollectionIndex];
    const groupedObjectsCount = currentUserGroups.reduce((acc, group) => acc + group.length, 0);
    const actualIsolatedCount = currentCollection.total - groupedObjectsCount;

    if (count === actualIsolatedCount) {
      playSound('success');
      setCorrectCount(prev => prev + 1);
      setFeedback({ type: 'success', message: `✅ Parfait ! ${count} objet${count > 1 ? 's' : ''} tout seul${count > 1 ? 's' : ''} !` });
      const newIsolatedCounts = [...isolatedCounts, count];
      setIsolatedCounts(newIsolatedCounts);

      setTimeout(() => {
        setFeedback(null);
        if (currentCollectionIndex === 0) {
          // Passer à la deuxième collection
          setCurrentCollectionIndex(1);
          setSelectedObjects([]);
          setStep('grouping');
          speak('Maintenant, on fait pareil pour la collection 2 ! Entoure des groupes de 5.');
        } else {
          // Passer à la comparaison
          setStep('comparison');
          speak('Maintenant, quelle collection a le plus d\'objets ?');
        }
      }, 2000);
    } else {
      playSound('error');
      setIncorrectCount(prev => prev + 1);
      setFeedback({ type: 'error', message: `❌ Recompte les objets tout seuls` });
      speak('Réessaie ! Compte bien les objets qui ne sont pas dans un groupe');
    }
  };

  const handleComparison = (collectionIndex) => {
    playSound('click');
    const correctIndex = collections[0].total > collections[1].total ? 0 : 1;

    if (collectionIndex === correctIndex) {
      playSound('success');
      setCorrectCount(prev => prev + 1);
      const childName = localStorage.getItem('childName');
      const successMessage = getSuccessMessage(childName);

      const total1 = collections[0].total;
      const total2 = collections[1].total;
      const explanation = `La collection ${correctIndex + 1} a ${collections[correctIndex].total} objets, et la collection ${correctIndex === 0 ? 2 : 1} a ${collections[correctIndex === 0 ? 1 : 0].total} objets.`;

      setFeedback({
        type: 'success',
        message: `🎉 ${successMessage} C'est la collection ${correctIndex + 1} qui a le plus d'objets !`
      });
      speak(`${successMessage} ${explanation}`);

      setTimeout(() => {
        generateExercise();
      }, 5000);
    } else {
      playSound('error');
      setIncorrectCount(prev => prev + 1);
      const childName = localStorage.getItem('childName');
      const errorMessage = getErrorMessage(childName);
      setFeedback({
        type: 'error',
        message: `❌ ${errorMessage} Réfléchis bien : collection 1 a ${collections[0].total} objets, collection 2 a ${collections[1].total} objets`
      });
      speak(`${errorMessage} Compte encore une fois les deux collections`);
    }
  };

  const isObjectInGroup = (objectId, collectionIndex) => {
    return userGroups[collectionIndex].some(group => group.includes(objectId));
  };

  const getGroupIndexForObject = (objectId, collectionIndex) => {
    return userGroups[collectionIndex].findIndex(group => group.includes(objectId));
  };

  const renderCollection = (collection, index, interactive = false, showCounts = false) => {
    const currentUserGroups = userGroups[index];
    const groupedObjects = new Set(currentUserGroups.flat());
    const groupColors = ['#E3F2FD', '#F3E5F5', '#E8F5E9', '#FFF3E0', '#FCE4EC'];
    const borderColors = ['#2196F3', '#9C27B0', '#4CAF50', '#FF9800', '#E91E63'];

    // Séparer les objets en groupes et isolés
    const isolatedObjects = collection.objects.filter(obj => !isObjectInGroup(obj.id, index));

    return (
      <div
        ref={interactive ? containerRef : null}
        onMouseDown={interactive ? handlePointerDown : null}
        onMouseMove={interactive ? handlePointerMove : null}
        onMouseUp={interactive ? handlePointerUp : null}
        onMouseLeave={interactive ? handlePointerUp : null}
        onTouchStart={interactive ? handlePointerDown : null}
        onTouchMove={interactive ? handlePointerMove : null}
        onTouchEnd={interactive ? handlePointerUp : null}
        onTouchCancel={interactive ? handlePointerUp : null}
        style={{
          border: index === currentCollectionIndex && step === 'grouping' ? '4px solid #667eea' : '2px solid #ddd',
          borderRadius: '15px',
          padding: '20px',
          backgroundColor: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          position: 'relative',
          cursor: interactive && step === 'grouping' ? 'crosshair' : 'default',
          touchAction: interactive && step === 'grouping' ? 'none' : 'auto'
        }}>
        <h3 style={{ textAlign: 'center', color: '#667eea', marginBottom: '20px' }}>
          Collection {index + 1}
        </h3>

        {/* Canvas pour dessiner */}
        {interactive && step === 'grouping' && (
          <canvas
            ref={canvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              pointerEvents: 'none',
              zIndex: 10
            }}
          />
        )}

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '15px',
          justifyContent: 'center',
          minHeight: '200px',
          alignItems: 'flex-start',
          position: 'relative'
        }}>
          {/* Afficher les groupes */}
          {currentUserGroups.map((group, gIndex) => (
            <div
              key={`group-${gIndex}`}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '5px',
                padding: '15px',
                backgroundColor: groupColors[gIndex % groupColors.length],
                borderRadius: '50%',
                border: `3px dashed ${borderColors[gIndex % borderColors.length]}`,
                width: '180px',
                height: '180px',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: interactive && step === 'grouping' ? 'none' : 'auto',
                userSelect: 'none'
              }}
            >
              {group.map((objId) => {
                const obj = collection.objects.find(o => o.id === objId);
                return (
                  <span
                    key={objId}
                    style={{
                      fontSize: '2rem'
                    }}
                  >
                    {obj.emoji}
                  </span>
                );
              })}
            </div>
          ))}

          {/* Afficher les objets isolés (non groupés) */}
          {isolatedObjects.map((obj) => {
            return (
              <div
                key={obj.id}
                ref={(el) => {
                  if (el) {
                    const rect = el.getBoundingClientRect();
                    const parentRect = containerRef.current?.getBoundingClientRect();
                    if (parentRect) {
                      objectPositionsRef.current[obj.id] = {
                        x: rect.left - parentRect.left + rect.width / 2,
                        y: rect.top - parentRect.top + rect.height / 2
                      };
                    }
                  }
                }}
                style={{
                  fontSize: '2.5rem',
                  padding: '5px',
                  pointerEvents: interactive && step === 'grouping' ? 'none' : 'auto',
                  userSelect: 'none'
                }}
              >
                {obj.emoji}
              </div>
            );
          })}
        </div>

        {/* Affichage du nombre de groupes créés */}
        {currentUserGroups.length > 0 && (
          <div style={{
            marginTop: '20px',
            padding: '10px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ margin: '5px 0', fontWeight: 'bold', color: '#667eea' }}>
              Groupes créés : {currentUserGroups.length}
            </p>
          </div>
        )}

        {showCounts && groupCounts[index] !== undefined && (
          <div style={{
            marginTop: '20px',
            padding: '10px',
            backgroundColor: '#e8f5e9',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ margin: '5px 0', color: '#4CAF50' }}>
              {groupCounts[index]} groupe{groupCounts[index] > 1 ? 's' : ''} de 5 = {groupCounts[index] * 5}
            </p>
            {isolatedCounts[index] !== undefined && (
              <>
                <p style={{ margin: '5px 0', color: '#4CAF50' }}>
                  {isolatedCounts[index]} objet{isolatedCounts[index] > 1 ? 's' : ''} tout seul{isolatedCounts[index] > 1 ? 's' : ''}
                </p>
                <p style={{ margin: '5px 0', fontWeight: 'bold', fontSize: '1.2rem', color: '#2E7D32' }}>
                  Total : {collection.total} objets
                </p>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  if (collections.length === 0) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div>
      <RewardCounter correctCount={correctCount} incorrectCount={incorrectCount} />

      <div className="header">
        <h1>🔢 Comparer des collections</h1>
        <p>Regroupe par 5, puis compte et compare !</p>
      </div>

      <div className="card">
        {step === 'introduction' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
              marginBottom: '30px'
            }}>
              {collections.map((collection, index) => renderCollection(collection, index))}
            </div>
            <p style={{ fontSize: '1.3rem', marginBottom: '20px', color: '#666' }}>
              Tu vas devoir regrouper les objets par paquets de 5 en les entourant !
            </p>
            <button
              className="btn btn-primary bounce"
              onClick={startGrouping}
              style={{ fontSize: '1.5rem', padding: '20px 40px' }}
            >
              🚀 Commencer à regrouper
            </button>
          </div>
        )}

        {step === 'grouping' && (
          <div>
            <div style={{ marginBottom: '20px' }}>
              {renderCollection(collections[currentCollectionIndex], currentCollectionIndex, true)}
            </div>

            <div style={{
              textAlign: 'center',
              padding: '15px',
              backgroundColor: '#E3F2FD',
              borderRadius: '10px',
              marginBottom: '20px'
            }}>
              <p style={{ fontSize: '1.3rem', color: '#1976D2', margin: '5px 0' }}>
                Dessine un cercle autour de 5 objets pour faire un groupe
              </p>
            </div>

            {feedback && (
              <div className={`feedback ${feedback.type === 'success' ? 'feedback-success' : 'feedback-error'}`}
                style={{ marginBottom: '20px' }}>
                {feedback.message}
              </div>
            )}

            <div style={{ textAlign: 'center' }}>
              <button
                className="btn btn-success"
                onClick={finishGrouping}
                style={{ fontSize: '1.3rem', padding: '15px 30px' }}
              >
                ✅ Terminer le regroupement
              </button>
            </div>
          </div>
        )}

        {step === 'counting-groups' && (
          <div>
            <div style={{ marginBottom: '30px' }}>
              {renderCollection(collections[currentCollectionIndex], currentCollectionIndex, false)}
            </div>

            {feedback && (
              <div className={`feedback ${feedback.type === 'success' ? 'feedback-success' : 'feedback-error'}`}
                style={{ marginBottom: '20px' }}>
                {feedback.message}
              </div>
            )}

            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '1.3rem', marginBottom: '20px', color: '#667eea' }}>
                Combien de groupes de 5 as-tu faits ?
              </p>
              <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {[0, 1, 2, 3, 4, 5].map(num => (
                  <button
                    key={num}
                    className="btn btn-primary"
                    onClick={() => handleGroupCount(num)}
                    style={{ fontSize: '1.5rem', padding: '20px 30px', minWidth: '80px' }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 'counting-isolated' && (
          <div>
            <div style={{ marginBottom: '30px' }}>
              {renderCollection(collections[currentCollectionIndex], currentCollectionIndex, false, true)}
            </div>

            {feedback && (
              <div className={`feedback ${feedback.type === 'success' ? 'feedback-success' : 'feedback-error'}`}
                style={{ marginBottom: '20px' }}>
                {feedback.message}
              </div>
            )}

            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '1.3rem', marginBottom: '20px', color: '#667eea' }}>
                Combien d'objets tout seuls (pas dans un groupe de 5) ?
              </p>
              <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <button
                    key={num}
                    className="btn btn-primary"
                    onClick={() => handleIsolatedCount(num)}
                    style={{ fontSize: '1.5rem', padding: '20px 30px', minWidth: '80px' }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 'comparison' && (
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
              marginBottom: '30px'
            }}>
              {collections.map((collection, index) => renderCollection(collection, index, false, true))}
            </div>

            {feedback && (
              <div className={`feedback ${feedback.type === 'success' ? 'feedback-success' : 'feedback-error'}`}
                style={{ marginBottom: '20px' }}>
                {feedback.message}
              </div>
            )}

            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '1.5rem', marginBottom: '20px', color: '#667eea', fontWeight: 'bold' }}>
                Quelle collection a le PLUS d'objets ?
              </p>
              <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
                <button
                  className="btn btn-success"
                  onClick={() => handleComparison(0)}
                  style={{ fontSize: '1.5rem', padding: '20px 40px' }}
                  disabled={selectedAnswer !== null}
                >
                  Collection 1<br/>({collections[0].total} objets)
                </button>
                <button
                  className="btn btn-success"
                  onClick={() => handleComparison(1)}
                  style={{ fontSize: '1.5rem', padding: '20px 40px' }}
                  disabled={selectedAnswer !== null}
                >
                  Collection 2<br/>({collections[1].total} objets)
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '40px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
          <button
            className="btn btn-warning"
            onClick={() => navigate('/subjects')}
          >
            🏠 Retour
          </button>
          <button
            className="btn btn-secondary"
            onClick={generateExercise}
          >
            ⏭️ Nouvelle collection
          </button>
        </div>
      </div>
    </div>
  );
};

export default CollectionComparison;
